import { Link, Navigate } from "react-router-dom";
import { Container, Eyebrow, Rule } from "@/brand/ui";
import { Footer } from "@/pages/Portal";
import { LEGAL_DOCS } from "@/i18n/legal";

const LOGO_HEADER = "/brand/logo-horizontal-header-clean.png";
const DOC_KEYS = ["privacidade", "termos", "disclaimer"];
const DOC_LABELS = { privacidade: "Política de Privacidade", termos: "Termos & Condições", disclaimer: "Disclaimer" };

function LegalHeader() {
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, background: "#ffffff", borderBottom: "1px solid var(--line)" }}>
      <Container>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", gap: 16 }}>
          <Link to="/" data-testid="legal-home-link" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img src={LOGO_HEADER} alt="Duarte Advocacia Internacional" style={{ height: 68, display: "block" }} />
          </Link>
          <Link to="/" data-testid="legal-back-link" style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--silver-dark)", textDecoration: "none", fontWeight: 500 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 18l-6-6 6-6" /></svg>
            Voltar ao site
          </Link>
        </div>
      </Container>
    </header>
  );
}

function Block({ block }) {
  if (block.t === "ul") {
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 4px", display: "flex", flexDirection: "column", gap: 12 }}>
        {block.v.map((item, i) => (
          <li key={i} style={{ display: "flex", alignItems: "baseline", gap: 14, fontSize: 15, lineHeight: 1.7, color: "var(--silver-dark)" }}>
            <span style={{ width: 5, height: 5, flexShrink: 0, marginTop: 9, background: "var(--gold-deep)", borderRadius: 999 }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p style={{ fontSize: 15, lineHeight: 1.8, color: "var(--silver-dark)", margin: 0 }}>{block.v}</p>;
}

function Section({ s }) {
  return (
    <div data-testid={`legal-section-${s.n}`} className="dai-legal-row" style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 48, paddingTop: 44, paddingBottom: 44, borderTop: "1px solid var(--line)" }}>
      <div style={{ fontFamily: "var(--font-serif)", fontSize: 40, fontWeight: 400, lineHeight: 1, color: "var(--gold-deep)" }}>{s.n}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>
        {s.tag && (
          <span style={{ alignSelf: "flex-start", fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--gold-deep)", border: "1px solid var(--line)", borderRadius: 2, padding: "5px 10px" }}>{s.tag}</span>
        )}
        <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 26, lineHeight: 1.2, color: "var(--navy)", letterSpacing: "0.005em", margin: 0 }}>{s.title}</h2>
        {s.blocks.map((b, i) => <Block key={i} block={b} />)}
      </div>
    </div>
  );
}

export default function LegalPage({ docKey }) {
  const doc = LEGAL_DOCS[docKey];
  if (!doc) return <Navigate to="/" replace />;
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <LegalHeader />

      <section style={{ background: "var(--navy)", color: "var(--off-white)", paddingTop: 80, paddingBottom: 72 }}>
        <Container narrow>
          <Eyebrow inverse gold>{doc.eyebrow}</Eyebrow>
          <h1 data-testid="legal-title" style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(38px, 5vw, 64px)", lineHeight: 1.05, letterSpacing: "0.005em", margin: "22px 0 0", color: "var(--off-white)" }}>{doc.title}</h1>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: "rgba(255,255,255,0.72)", maxWidth: 640, margin: "22px 0 0", fontWeight: 300 }}>{doc.subtitle}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 28, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>
            <Rule width={28} color="var(--gold)" /> {doc.updated}
          </div>
        </Container>
      </section>

      <section style={{ background: "var(--bg-elevated)", padding: "40px 0 90px", borderBottom: "1px solid var(--line)" }}>
        <Container narrow>
          <div data-testid="legal-doc-switch" style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "36px 0 8px" }}>
            {DOC_KEYS.map((k) => (
              <Link key={k} to={`/${k}`} data-testid={`legal-tab-${k}`}
                style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 500, padding: "9px 15px", borderRadius: 2, textDecoration: "none",
                  border: `1px solid ${k === docKey ? "var(--navy)" : "var(--line)"}`,
                  background: k === docKey ? "var(--navy)" : "transparent",
                  color: k === docKey ? "var(--off-white)" : "var(--silver-dark)" }}>
                {DOC_LABELS[k]}
              </Link>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            {doc.sections.map((s) => <Section key={s.n} s={s} />)}
            <div style={{ borderTop: "1px solid var(--line)" }} />
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
